import * as fabric from 'fabric';
import type { ResolvedElement } from '../types/preview';
import type {
  ExportConfig,
  ExportOutputs,
  ExportPacket,
  ExportLayer,
  HeroConfig,
  PacketLayerType,
  PacketHtmlTag,
  ButtonUrlAssignment,
} from '../types/export';
import { JPEG_QUALITY_MAP, generatePacketFilename, UNBRANDED_LEGACY_ID } from '../types/export';
import type {
  CanvasElement,
  TextElement,
  ShapeElement,
  ImageElement,
  ButtonElement,
  LineElement,
  PathElement,
  SvgElement,
} from '../types/index';
import { shadowConfigToFabric, fillConfigToFabric } from './fabricVisuals';
import { getShapePoints } from './shapePoints';
import { getDashArray } from './elementDefaults';
import {
  loadCustomFont,
  resolvePostscriptName,
  getFontVariantById,
  isPostscriptOnServer,
  type CustomFont,
} from './fontLoader';
import { useFontStore } from '../stores/fontStore';
import { useStudioStore } from '../stores/studioStore';
import { useMappingStore } from '../stores/mappingStore';
import type { EnhancedFieldMapping } from '../types/mapping';
import { resolveTextPathData } from './pathUtils';
import { measureTextBlockHeight } from './textMetrics';
import { applyTextFit } from './mappingValueApplicator';
import { reconcileReadingOrder, isReadableElement } from './readingOrder';

// Mirrors applyTextTransform() in CanvasWorkspace.tsx. Must stay in sync so
// exported text matches exactly what the editor renders.
function applyTextTransformForExport(
  raw: string,
  transform: TextElement['textTransform'] | undefined
): string {
  if (!raw || !transform || transform === 'none') return raw;
  switch (transform) {
    case 'uppercase':
      return raw.toUpperCase();
    case 'lowercase':
      return raw.toLowerCase();
    case 'capitalize':
      return raw.replace(/\b\w/g, (c) => c.toUpperCase());
    default:
      return raw;
  }
}

function setCrossOriginOnImages(obj: fabric.FabricObject): void {
  if (obj instanceof fabric.FabricImage) {
    const el = obj.getElement() as HTMLImageElement | null;
    if (el && !el.crossOrigin) {
      el.crossOrigin = 'anonymous';
    }
  }
  if (obj instanceof fabric.Group) {
    for (const child of obj.getObjects()) {
      setCrossOriginOnImages(child);
    }
  }
}

async function reloadEmbeddedImagesWithCors(obj: fabric.FabricObject): Promise<void> {
  const images: fabric.FabricImage[] = [];

  function collectImages(node: fabric.FabricObject): void {
    if (node instanceof fabric.FabricImage) {
      images.push(node);
    }
    if (node instanceof fabric.Group) {
      for (const child of node.getObjects()) {
        collectImages(child);
      }
    }
  }

  collectImages(obj);

  if (images.length === 0) return;

  console.log('[Export] Reloading', images.length, 'embedded SVG images with CORS');

  const reloadPromises = images.map((fabricImg) => {
    return new Promise<void>((resolve) => {
      const currentEl = fabricImg.getElement() as HTMLImageElement | null;
      const src = currentEl?.src;

      if (!src) {
        console.warn('[Export] FabricImage has no src, skipping CORS reload');
        resolve();
        return;
      }

      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        console.warn('[Export] Image CORS reload timed out', { src: src.substring(0, 100) });
        resolve();
      }, 10000);

      newImg.onload = () => {
        clearTimeout(timeout);
        try {
          (fabricImg as any).setElement(newImg);
          console.log('[Export] Image CORS reload OK', { src: src.substring(0, 60) });
        } catch (err) {
          console.error('[Export] setElement failed during CORS reload', err);
        }
        resolve();
      };

      newImg.onerror = (err) => {
        clearTimeout(timeout);
        console.error('[Export] Image CORS reload FAILED — remote server likely missing Access-Control-Allow-Origin', {
          src: src.substring(0, 100),
          error: err,
        });
        resolve();
      };

      newImg.src = src;
    });
  });

  await Promise.all(reloadPromises);
}

// ============================================================================
// FONT PRELOADING
// ============================================================================

async function ensureFontsLoaded(elements: ResolvedElement[]): Promise<void> {
  const families = new Set<string>();

  for (const re of elements) {
    const el = re.resolved;
    if (!el.visible) continue;

    if (el.type === 'text') {
      const t = el as TextElement;
      if (t.fontFamily) families.add(t.fontFamily);
    } else if (el.type === 'button') {
      const b = el as ButtonElement;
      if (b.fontFamily) families.add(b.fontFamily);
    }
  }

  if (families.size === 0) return;

  const loadResults = await Promise.all(
    Array.from(families).map(async (family) => {
      try {
        const ok = await loadCustomFont(family);
        return { family, ok };
      } catch (err) {
        console.warn('[Export] Font preload failed', { family, err });
        return { family, ok: false };
      }
    })
  );

  const missing = loadResults.filter((r) => !r.ok).map((r) => r.family);
  if (missing.length > 0) {
    console.warn('[Export] Some fonts not preloaded (may fall back):', missing);
  }

  try {
    await document.fonts.ready;
  } catch {
    // ignore
  }
}

// ============================================================================
// EXPORT-BLOCKING FONT VALIDATION (CS Rule E.4)
// ============================================================================

async function validateFontsBeforeExport(
  elements: ResolvedElement[]
): Promise<void> {
  const customFonts = useFontStore.getState().fonts;
  const errors: string[] = [];

  for (const re of elements) {
    const element = re.resolved;
    if (element.type !== 'text' && element.type !== 'button') continue;
    if (!element.visible) continue;

    const textEl = element as TextElement | ButtonElement;
    const fontFamily = textEl.fontFamily;

    const targetWeight = String(textEl.fontWeight ?? 400);
    const targetStyle = (textEl as unknown as { fontStyle?: string }).fontStyle === 'italic' ? 'italic' : 'normal';

    if (!fontFamily || fontFamily.trim() === '') {
      errors.push(
        `Element "${textEl.name || textEl.id}" has no fontFamily set. Assign a font before exporting.`
      );
      continue;
    }

    const familyVariants = customFonts.filter(
      (f) => f.fontFamily === fontFamily && f.isActive
    );
    if (familyVariants.length === 0) {
      errors.push(
        `Element "${textEl.name || textEl.id}" uses font "${fontFamily}" which is not in the Font Manager. Upload the font or reassign the element.`
      );
      continue;
    }

    let resolvedVariant = familyVariants.find(
      (f) => f.fontWeight === targetWeight && f.fontStyle === targetStyle
    );

    if (!resolvedVariant) {
      const sameStyle = familyVariants.filter((f) => f.fontStyle === targetStyle);
      if (sameStyle.length > 0) {
        const tw = parseInt(targetWeight, 10) || 400;
        sameStyle.sort((a, b) =>
          Math.abs(parseInt(a.fontWeight, 10) - tw) - Math.abs(parseInt(b.fontWeight, 10) - tw)
        );
        resolvedVariant = sameStyle[0];
        console.warn(
          `[ExportValidator] ${fontFamily} ${targetWeight}/${targetStyle} not found; ` +
          `using closest: ${resolvedVariant.fontWeight}/${resolvedVariant.fontStyle}`
        );
      } else if (familyVariants.length > 0) {
        resolvedVariant = familyVariants[0];
        console.warn(
          `[ExportValidator] ${fontFamily} ${targetWeight}/${targetStyle} not found; ` +
          `using fallback variant: ${resolvedVariant.fontWeight}/${resolvedVariant.fontStyle}`
        );
      }
    }

    if (!resolvedVariant) {
      errors.push(
        `Element "${textEl.name || textEl.id}" uses ${fontFamily} ${targetWeight}/${targetStyle} which has no usable variant uploaded.`
      );
      continue;
    }

    const checkWeight = resolvedVariant.fontWeight || '400';
    const checkStyle = resolvedVariant.fontStyle === 'italic' ? 'italic' : '';
    const isLoaded = document.fonts.check(
      `${checkStyle} ${checkWeight} 16px "${fontFamily}"`.trim()
    );
    if (!isLoaded) {
      const looseCheck = document.fonts.check(`16px "${fontFamily}"`);
      if (!looseCheck) {
        errors.push(
          `Element "${textEl.name || textEl.id}" font ${fontFamily} ${targetWeight}/${targetStyle} has not finished loading. Wait a moment and retry.`
        );
      }
    }
  }

  if (errors.length > 0) {
    const summary = errors.length === 1
      ? errors[0]
      : `${errors.length} font issues:\n` + errors.map((e, i) => `${i + 1}. ${e}`).join('\n');
    throw new Error(`Export blocked — font validation failed:\n${summary}`);
  }
}

// ============================================================================
// BACKGROUND IMAGE RENDERING
// ============================================================================

export async function generateBackgroundImage(
  elements: ResolvedElement[],
  width: number,
  height: number,
  config: ExportConfig,
  backgroundColor?: string
): Promise<Blob> {
  const backgroundElements = elements
    .filter((re) => re.resolved.layer === 'BACKGROUND_LAYER' && re.resolved.visible)
    .sort((a, b) => a.resolved.zIndex - b.resolved.zIndex);

  await ensureFontsLoaded(backgroundElements);

  const canvasEl = document.createElement('canvas');
  canvasEl.width = width * config.scale;
  canvasEl.height = height * config.scale;

  const fabricCanvas = new fabric.StaticCanvas(canvasEl, {
    width: width * config.scale,
    height: height * config.scale,
    backgroundColor: backgroundColor || '#ffffff',
  });

  if (config.scale !== 1) {
    fabricCanvas.setZoom(config.scale);
  }

  for (const re of backgroundElements) {
    const obj = await createFabricObject(re.resolved);
    if (obj) {
      setCrossOriginOnImages(obj);
      await reloadEmbeddedImagesWithCors(obj);
      fabricCanvas.add(obj);
    }
  }

  fabricCanvas.renderAll();

  let dataUrl: string;
  try {
    console.log('[Export] Calling toDataURL', {
      format: config.format,
      quality: config.quality,
      canvasObjects: fabricCanvas.getObjects().length,
    });
    dataUrl = fabricCanvas.toDataURL({
      format: config.format,
      quality: JPEG_QUALITY_MAP[config.quality],
      multiplier: 1,
    });
    console.log('[Export] toDataURL success', { dataUrlSizeKB: Math.round(dataUrl.length / 1024) });
  } catch (err) {
    console.error('[Export] toDataURL FAILED', {
      errorName: err instanceof Error ? err.name : 'unknown',
      errorMessage: err instanceof Error ? err.message : String(err),
      canvasObjects: fabricCanvas.getObjects().length,
      fullError: err,
    });
    fabricCanvas.dispose();
    if (err instanceof DOMException && err.name === 'SecurityError') {
      throw new Error(
        'Export failed: canvas tainted by cross-origin image. ' +
        'One or more embedded images were loaded from a server that does not send Access-Control-Allow-Origin headers. ' +
        'Check the browser console for [Export] Image CORS reload FAILED entries.'
      );
    }
    throw new Error(`Export toDataURL failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  const blob = await dataUrlToBlob(dataUrl);

  fabricCanvas.dispose();

  return blob;
}

// ============================================================================
// COMPOSITE IMAGE RENDERING (BACKGROUND + OVERLAY MERGED)
// ============================================================================

export async function generateCompositeImage(
  elements: ResolvedElement[],
  width: number,
  height: number,
  config: ExportConfig,
  backgroundColor?: string
): Promise<Blob> {
  const allElements = elements
    .filter((re) => re.resolved.visible)
    .sort((a, b) => {
      const layerOrder = (layer: string) => (layer === 'BACKGROUND_LAYER' ? 0 : 1);
      const aLayer = layerOrder(a.resolved.layer);
      const bLayer = layerOrder(b.resolved.layer);
      if (aLayer !== bLayer) return aLayer - bLayer;
      return a.resolved.zIndex - b.resolved.zIndex;
    });

  console.log('[Export] Composite render starting', {
    totalElements: allElements.length,
    backgroundCount: allElements.filter(re => re.resolved.layer === 'BACKGROUND_LAYER').length,
    overlayCount: allElements.filter(re => re.resolved.layer === 'OVERLAY_LAYER').length,
  });

  await ensureFontsLoaded(allElements);

  const canvasEl = document.createElement('canvas');
  canvasEl.width = width * config.scale;
  canvasEl.height = height * config.scale;

  const fabricCanvas = new fabric.StaticCanvas(canvasEl, {
    width: width * config.scale,
    height: height * config.scale,
    backgroundColor: backgroundColor || '#ffffff',
  });

  if (config.scale !== 1) {
    fabricCanvas.setZoom(config.scale);
  }

  for (const re of allElements) {
    const obj = await createFabricObject(re.resolved);
    if (obj) {
      setCrossOriginOnImages(obj);
      await reloadEmbeddedImagesWithCors(obj);
      fabricCanvas.add(obj);
    }
  }

  fabricCanvas.renderAll();

  let dataUrl: string;
  try {
    console.log('[Export] Composite toDataURL', {
      format: config.format,
      quality: config.quality,
      canvasObjects: fabricCanvas.getObjects().length,
    });
    dataUrl = fabricCanvas.toDataURL({
      format: config.format,
      quality: JPEG_QUALITY_MAP[config.quality],
      multiplier: 1,
    });
    console.log('[Export] Composite success', { dataUrlSizeKB: Math.round(dataUrl.length / 1024) });
  } catch (err) {
    console.error('[Export] Composite toDataURL FAILED', {
      errorName: err instanceof Error ? err.name : 'unknown',
      errorMessage: err instanceof Error ? err.message : String(err),
      fullError: err,
    });
    fabricCanvas.dispose();
    if (err instanceof DOMException && err.name === 'SecurityError') {
      throw new Error(
        'Composite export failed: canvas tainted by cross-origin image. ' +
        'Check the browser console for [Export] Image CORS reload FAILED entries.'
      );
    }
    throw new Error(`Composite toDataURL failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  const blob = await dataUrlToBlob(dataUrl);
  fabricCanvas.dispose();
  return blob;
}

function applyElementShadow(obj: fabric.FabricObject, element: CanvasElement): void {
  if (element.shadow?.enabled) {
    const fabricShadow = shadowConfigToFabric(element.shadow);
    if (fabricShadow) obj.set('shadow', fabricShadow);
  }
}

function hasResolvedFillMapping(
  element: CanvasElement,
  elementMappings: Record<string, EnhancedFieldMapping[]>,
  property: 'fillColor' | 'buttonFillColor' | 'strokeColor'
): boolean {
  const mappings = elementMappings[element.id];
  if (!mappings || mappings.length === 0) return false;
  return mappings.some((m) => m.targetProperty === property);
}

function resolveShapeFill(
  shapeEl: ShapeElement,
  width: number,
  height: number
): string | fabric.Gradient<'linear'> | fabric.Gradient<'radial'> {
  const { elementMappings } = useMappingStore.getState();
  if (hasResolvedFillMapping(shapeEl, elementMappings, 'fillColor')) {
    return shapeEl.fill || '#cccccc';
  }
  if (shapeEl.shapeFill) {
    return fillConfigToFabric(shapeEl.shapeFill, width, height);
  }
  return shapeEl.fill || '#cccccc';
}

async function createFabricObject(
  element: CanvasElement
): Promise<fabric.FabricObject | null> {
  const { type, x, y, width, height, rotation, opacity } = element;

  const baseOptions = {
    left: x,
    top: y,
    width,
    height,
    angle: rotation || 0,
    opacity: opacity ?? 1,
    selectable: false,
    evented: false,
  };

  let obj: fabric.FabricObject | null = null;

  switch (type) {
    case 'text': {
      const textEl = element as TextElement;
      const textFill = (textEl.textFill && textEl.textFill.type === 'gradient')
        ? fillConfigToFabric(textEl.textFill, width, height)
        : textEl.color || '#000000';

      // Parity with CanvasWorkspace: the editor applies textTransform to the
      // rendered string (Fabric has no native text-transform property) and
      // maps letterSpacing -> charSpacing * 10. lineHeight is a unitless
      // multiplier; fontStyle/underline/linethrough must be set explicitly.
      const renderedText = applyTextTransformForExport(
        textEl.text || '',
        textEl.textTransform
      );
      const decoration = textEl.textDecoration;

      const effectivePathData = (textEl.textPath
        ? resolveTextPathData(textEl.textPath.pathElementId, useStudioStore.getState().canvasState.elements)
        : null) || textEl.pathData;

      if (effectivePathData) {
        const pStartOffset = textEl.textPath?.startOffset ?? textEl.pathStartOffset ?? 0;
        const pSide = textEl.textPath?.side ?? textEl.pathSide ?? 'left';
        const pAlign = textEl.textPath?.align ?? textEl.pathAlign ?? 'baseline';
        const pathObj = new fabric.Path(effectivePathData, {
          fill: '',
          stroke: '',
          visible: false,
        });
        obj = new fabric.FabricText(renderedText, {
          ...baseOptions,
          fontFamily: textEl.fontFamily || 'Arial',
          fontSize: textEl.fontSize || 16,
          fontWeight: String(textEl.fontWeight || 400),
          fontStyle: textEl.fontStyle || 'normal',
          fill: textFill,
          textAlign: textEl.textAlign || 'left',
          lineHeight: textEl.lineHeight ?? 1.2,
          charSpacing: (textEl.letterSpacing ?? 0) * 10,
          underline: decoration === 'underline',
          linethrough: decoration === 'line-through',
          path: pathObj,
          pathStartOffset: pStartOffset,
          pathSide: pSide,
          pathAlign: pAlign,
        });
      } else {
        const textbox = new fabric.Textbox(renderedText, {
          ...baseOptions,
          fontFamily: textEl.fontFamily || 'Arial',
          fontSize: textEl.fontSize || 16,
          fontWeight: String(textEl.fontWeight || 400),
          fontStyle: textEl.fontStyle || 'normal',
          fill: textFill,
          textAlign: textEl.textAlign || 'left',
          lineHeight: textEl.lineHeight ?? 1.2,
          charSpacing: (textEl.letterSpacing ?? 0) * 10,
          underline: decoration === 'underline',
          linethrough: decoration === 'line-through',
        });
        // The editor always calls initDimensions() after property set so the
        // wrap height matches what the user sees. Without it, Fabric's default
        // line height (1.2) wins over the stored lineHeight multiplier.
        textbox.initDimensions();
        textbox.setCoords();
        obj = textbox;
      }
      if (textEl.strokeColor && textEl.textStrokeWidth) {
        obj.set('stroke', textEl.strokeColor);
        obj.set('strokeWidth', textEl.textStrokeWidth);
      }
      break;
    }

    case 'shape': {
      const shapeEl = element as ShapeElement;
      const shapeFill = resolveShapeFill(shapeEl, width, height);
      const borderWidth = shapeEl.border?.width ?? shapeEl.strokeWidth ?? 0;
      const borderColor = shapeEl.border?.color ?? shapeEl.stroke ?? undefined;
      const shapeOptions = {
        ...baseOptions,
        fill: shapeFill,
        stroke: borderColor,
        strokeWidth: borderWidth,
        opacity: (shapeEl.fillOpacity ?? 1) * (opacity ?? 1),
      };

      if (shapeEl.border?.style === 'dashed') {
        (shapeOptions as Record<string, unknown>).strokeDashArray = [8, 4];
      } else if (shapeEl.border?.style === 'dotted') {
        (shapeOptions as Record<string, unknown>).strokeDashArray = [2, 2];
      }

      switch (shapeEl.shapeType) {
        case 'rectangle':
        case 'rounded-rect': {
          const fallback = shapeEl.shapeType === 'rounded-rect'
            ? (shapeEl.borderRadius ?? 8)
            : (shapeEl.borderRadius ?? 0);
          const radius = shapeEl.border?.radius
            ? shapeEl.border.radius.linked
              ? shapeEl.border.radius.topLeft
              : Math.max(
                  shapeEl.border.radius.topLeft,
                  shapeEl.border.radius.topRight,
                  shapeEl.border.radius.bottomRight,
                  shapeEl.border.radius.bottomLeft
                )
            : fallback;
          obj = new fabric.Rect({ ...shapeOptions, rx: radius, ry: radius });
          break;
        }
        case 'circle':
          obj = new fabric.Ellipse({ ...shapeOptions, rx: width / 2, ry: height / 2 });
          break;
        case 'polygon': {
          const points = [
            { x: width / 2, y: 0 },
            { x: width, y: height },
            { x: 0, y: height },
          ];
          obj = new fabric.Polygon(points, { ...shapeOptions, left: x, top: y });
          break;
        }
        default: {
          const customPoints = getShapePoints(shapeEl.shapeType, width, height);
          if (customPoints) {
            obj = new fabric.Polygon(customPoints, { ...shapeOptions, left: x, top: y });
          } else {
            obj = new fabric.Rect(shapeOptions);
          }
        }
      }
      break;
    }

    case 'line': {
      const lineEl = element as LineElement;
      obj = new fabric.Line(
        [
          lineEl.x + lineEl.x1,
          lineEl.y + lineEl.y1,
          lineEl.x + lineEl.x2,
          lineEl.y + lineEl.y2,
        ],
        {
          stroke: lineEl.stroke,
          strokeWidth: lineEl.strokeWidth,
          strokeDashArray: getDashArray(lineEl.strokeDashStyle, lineEl.strokeWidth),
          strokeLineCap: lineEl.lineCap,
          opacity: lineEl.opacity,
          selectable: false,
          evented: false,
        }
      );
      break;
    }

    case 'path': {
      const pathEl = element as PathElement;
      if (!pathEl.pathData) break;
      obj = new fabric.Path(pathEl.pathData, {
        left: x,
        top: y,
        fill: pathEl.closed ? (pathEl.fill || 'transparent') : 'transparent',
        stroke: pathEl.stroke || '#ffffff',
        strokeWidth: pathEl.strokeWidth ?? 2,
        strokeDashArray: getDashArray(pathEl.strokeDashStyle || 'solid', pathEl.strokeWidth ?? 2),
        strokeLineCap: pathEl.lineCap || 'round',
        strokeLineJoin: pathEl.lineJoin || 'miter',
        opacity: (opacity ?? 1) * (pathEl.closed ? (pathEl.fillOpacity ?? 1) : 1),
        angle: rotation || 0,
        selectable: false,
        evented: false,
      });
      break;
    }

    case 'image': {
      const imgEl = element as ImageElement;
      if (!imgEl.src) return null;

      try {
        const img = await fabric.FabricImage.fromURL(imgEl.src, {
          crossOrigin: 'anonymous',
        });
        img.set({
          ...baseOptions,
          scaleX: width / (img.width || width),
          scaleY: height / (img.height || height),
        });
        obj = img;
      } catch {
        return null;
      }
      break;
    }

    case 'button': {
      const btnEl = element as ButtonElement;
      const group: fabric.FabricObject[] = [];

      const { elementMappings } = useMappingStore.getState();
      const buttonFillResolved = hasResolvedFillMapping(btnEl, elementMappings, 'buttonFillColor');
      const btnFill = buttonFillResolved
        ? (btnEl.backgroundColor || '#a855f7')
        : (btnEl.buttonFill
            ? fillConfigToFabric(btnEl.buttonFill, width, height)
            : btnEl.backgroundColor || '#a855f7');
      const btnRadius = btnEl.border?.radius
        ? btnEl.border.radius.linked
          ? btnEl.border.radius.topLeft
          : Math.max(
              btnEl.border.radius.topLeft,
              btnEl.border.radius.topRight,
              btnEl.border.radius.bottomRight,
              btnEl.border.radius.bottomLeft
            )
        : btnEl.borderRadius ?? 4;

      const bg = new fabric.Rect({
        left: 0,
        top: 0,
        width,
        height,
        fill: btnFill,
        rx: btnRadius,
        ry: btnRadius,
      });
      if (btnEl.border && btnEl.border.width > 0) {
        bg.set('stroke', btnEl.border.color);
        bg.set('strokeWidth', btnEl.border.width);
      }
      group.push(bg);

      if (btnEl.text) {
        const btnDecoration = (btnEl as unknown as { textDecoration?: TextElement['textDecoration'] }).textDecoration;
        const btnTransform = (btnEl as unknown as { textTransform?: TextElement['textTransform'] }).textTransform;
        const btnLetterSpacing = (btnEl as unknown as { letterSpacing?: number }).letterSpacing ?? 0;
        const btnLineHeight = (btnEl as unknown as { lineHeight?: number }).lineHeight ?? 1.2;
        const btnFontStyle = (btnEl as unknown as { fontStyle?: 'normal' | 'italic' }).fontStyle ?? 'normal';
        const text = new fabric.Text(
          applyTextTransformForExport(btnEl.text, btnTransform),
          {
            left: width / 2,
            top: height / 2,
            fontFamily: btnEl.fontFamily || 'Arial',
            fontSize: btnEl.fontSize || 14,
            fontWeight: String(btnEl.fontWeight || 600),
            fontStyle: btnFontStyle,
            fill: btnEl.color || '#ffffff',
            originX: 'center',
            originY: 'center',
            lineHeight: btnLineHeight,
            charSpacing: btnLetterSpacing * 10,
            underline: btnDecoration === 'underline',
            linethrough: btnDecoration === 'line-through',
          }
        );
        group.push(text);
      }

      obj = new fabric.Group(group, {
        left: x,
        top: y,
        angle: rotation || 0,
        opacity: opacity ?? 1,
        selectable: false,
        evented: false,
      });
      break;
    }

    case 'svg': {
      const svgEl = element as SvgElement;
      try {
        const { objects, options } = await fabric.loadSVGFromString(svgEl.svgMarkup);
        const validObjects = objects.filter((o): o is fabric.FabricObject => o !== null);
        if (validObjects.length === 0) {
          console.warn('[Export] SVG loadSVGFromString returned 0 valid objects', { elementId: svgEl.id });
          return null;
        }

        for (const svgObj of validObjects) {
          setCrossOriginOnImages(svgObj);
        }

        const group = fabric.util.groupSVGElements(validObjects, options);

        setCrossOriginOnImages(group);

        group.set({
          left: x,
          top: y,
          scaleX: width / (svgEl.originalWidth || 1),
          scaleY: height / (svgEl.originalHeight || 1),
          angle: rotation || 0,
          opacity: opacity ?? 1,
          selectable: false,
          evented: false,
        });

        await reloadEmbeddedImagesWithCors(group);

        obj = group;
        console.log('[Export] SVG element processed', {
          elementId: svgEl.id,
          childCount: validObjects.length,
        });
      } catch (err) {
        console.error('[Export] SVG load failed', { elementId: svgEl.id, error: err });
        return null;
      }
      break;
    }

    default:
      return null;
  }

  if (obj) {
    applyElementShadow(obj, element);
  }

  return obj;
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

// ============================================================================
// PACKET OVERLAY GENERATION (JSON .txt for downstream Packet tool)
// ============================================================================

function slugifyLayerId(name: string, usedIds: Set<string>): string {
  let slug = name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  if (!slug) slug = 'layer';

  let finalSlug = slug;
  let counter = 2;
  while (usedIds.has(finalSlug)) {
    finalSlug = `${slug}-${counter}`;
    counter++;
  }
  usedIds.add(finalSlug);
  return finalSlug;
}

function assignHtmlTags(elements: CanvasElement[]): Map<string, PacketHtmlTag> {
  const tagMap = new Map<string, PacketHtmlTag>();
  const textElements: { id: string; fontSize: number }[] = [];

  for (const el of elements) {
    if (el.type === 'text') {
      textElements.push({ id: el.id, fontSize: (el as TextElement).fontSize || 16 });
    } else if (el.type === 'button') {
      tagMap.set(el.id, 'a');
    } else if (el.type === 'image') {
      tagMap.set(el.id, 'img');
    } else if (el.type === 'shape' || el.type === 'svg' || el.type === 'path') {
      tagMap.set(el.id, 'div');
    }
  }

  textElements.sort((a, b) => b.fontSize - a.fontSize);

  const headingLevels: PacketHtmlTag[] = ['h1', 'h2', 'h3'];
  let headingIdx = 0;
  let lastSize = -1;

  for (const item of textElements) {
    if (headingIdx < headingLevels.length) {
      if (item.fontSize === lastSize && headingIdx > 0) {
        tagMap.set(item.id, headingLevels[headingIdx - 1]);
      } else {
        tagMap.set(item.id, headingLevels[headingIdx]);
        lastSize = item.fontSize;
        headingIdx++;
      }
    } else {
      tagMap.set(item.id, 'p');
    }
  }

  return tagMap;
}

function getLayerType(element: CanvasElement): PacketLayerType {
  switch (element.type) {
    case 'text':
      return 'text';
    case 'button':
      return 'link';
    case 'image':
      return 'image';
    case 'svg':
      return 'shape';
    default:
      return 'shape';
  }
}

function getElementContent(element: CanvasElement): string {
  if (element.type === 'text') return (element as TextElement).text || '';
  if (element.type === 'button') return (element as ButtonElement).text || '';
  if (element.type === 'svg') return '';
  return '';
}

interface ExportFontResolution {
  postscriptName: string;
  matched: boolean;
  exactMatch: boolean;
  serverAvailable: boolean;
}

const exportFontWarnings: {
  requestedFamily: string;
  requestedWeight: string;
  requestedStyle: string;
  emittedPostscript: string;
  reason: 'unmatched' | 'inexact-variant' | 'missing-on-server';
}[] = [];

export function consumeExportFontWarnings(): typeof exportFontWarnings {
  const out = exportFontWarnings.slice();
  exportFontWarnings.length = 0;
  return out;
}

function resolveExportFontFamily(
  fontFamily: string,
  fontWeight: number | string,
  fontStyle: string,
  _fonts: CustomFont[],
  variantId?: string | null
): ExportFontResolution {
  // Prefer the explicit variant id set by the editor. When present, the
  // postscript face already encodes weight + style, so the packet CSS can
  // emit `font-family: '<postscript>'` with no weight/style overrides and
  // the browser never has to synthesize bold/italic.
  if (variantId) {
    const variant = getFontVariantById(variantId);
    if (variant) {
      return {
        postscriptName: variant.postscriptName,
        matched: true,
        exactMatch: true,
        serverAvailable:
          variant.serverAvailable && isPostscriptOnServer(variant.postscriptName),
      };
    }
  }
  const r = resolvePostscriptName(fontFamily, fontWeight, fontStyle);
  if (!r.matched) {
    exportFontWarnings.push({
      requestedFamily: fontFamily,
      requestedWeight: String(fontWeight ?? '400'),
      requestedStyle: String(fontStyle ?? 'normal'),
      emittedPostscript: r.postscriptName,
      reason: 'unmatched',
    });
  } else if (!r.exactMatch) {
    exportFontWarnings.push({
      requestedFamily: fontFamily,
      requestedWeight: String(fontWeight ?? '400'),
      requestedStyle: String(fontStyle ?? 'normal'),
      emittedPostscript: r.postscriptName,
      reason: 'inexact-variant',
    });
  } else if (!r.serverAvailable) {
    exportFontWarnings.push({
      requestedFamily: fontFamily,
      requestedWeight: String(fontWeight ?? '400'),
      requestedStyle: String(fontStyle ?? 'normal'),
      emittedPostscript: r.postscriptName,
      reason: 'missing-on-server',
    });
  }
  return {
    postscriptName: r.postscriptName,
    matched: r.matched,
    exactMatch: r.exactMatch,
    serverAvailable: r.serverAvailable,
  };
}

// Convert a FillConfig (solid or gradient) to a CSS `background` value so the
// packet HTML paints identically to what the canvas renders.
function fillConfigToCss(
  fill: { type: 'solid' | 'gradient'; color: string; gradient?: { type: 'linear' | 'radial'; angle: number; stops: { offset: number; color: string }[] } } | undefined,
  fallback: string
): string {
  if (!fill) return fallback;
  if (fill.type === 'solid') return fill.color || fallback;
  const g = fill.gradient;
  if (!g || !g.stops || g.stops.length === 0) return fill.color || fallback;
  const stops = g.stops
    .slice()
    .sort((a, b) => a.offset - b.offset)
    .map((s) => `${s.color} ${Math.round((s.offset ?? 0) * 100)}%`)
    .join(', ');
  if (g.type === 'radial') return `radial-gradient(circle, ${stops})`;
  return `linear-gradient(${g.angle ?? 0}deg, ${stops})`;
}

function shadowToBoxShadowCss(
  shadow: { enabled: boolean; color: string; offsetX: number; offsetY: number; blur: number } | undefined
): string | null {
  if (!shadow || !shadow.enabled) return null;
  return `${shadow.offsetX || 0}px ${shadow.offsetY || 0}px ${shadow.blur || 0}px ${shadow.color || 'rgba(0,0,0,0.25)'}`;
}

function shadowToTextShadowCss(
  shadow: { enabled: boolean; color: string; offsetX: number; offsetY: number; blur: number } | undefined
): string | null {
  if (!shadow || !shadow.enabled) return null;
  return `${shadow.offsetX || 0}px ${shadow.offsetY || 0}px ${shadow.blur || 0}px ${shadow.color || 'rgba(0,0,0,0.25)'}`;
}

function generateRawCSS(element: CanvasElement, allElements: CanvasElement[]): string {
  const styles: string[] = [];
  const fonts = useFontStore.getState().fonts;

  styles.push('position: absolute');
  styles.push(`top: ${Math.round(element.y)}px`);
  styles.push(`left: ${Math.round(element.x)}px`);

  // Width emission: text/button use Math.ceil + advance-measurement safety net
  // so half-pixel rounding can never clip the trailing glyph (e.g. the
  // period in "bookstore re-imagined."). Non-text elements keep Math.round.
  let emittedWidth = element.width ?? 0;
  if (element.width && (element.type === 'text' || element.type === 'button')) {
    const t = element as TextElement | ButtonElement;
    const text = (t as { text?: string }).text || '';
    const fontSize = t.fontSize || 16;
    const fontFamily = t.fontFamily || 'Arial';
    const fontWeight = (t as { fontWeight?: number | string }).fontWeight ?? 400;
    const fontStyle = (t as { fontStyle?: string }).fontStyle || 'normal';
    let measuredAdvance = 0;
    try {
      if (typeof document !== 'undefined') {
        const c = document.createElement('canvas');
        const cctx = c.getContext('2d');
        if (cctx) {
          cctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;
          for (const line of text.split('\n')) {
            const w = cctx.measureText(line || 'M').width;
            if (w > measuredAdvance) measuredAdvance = w;
          }
        }
      }
    } catch {
      // measurement unavailable; rely on stored width
    }
    if (measuredAdvance > 0) {
      const lastGlyph = text.trimEnd().slice(-1);
      const punctBump = lastGlyph && /[.,!?;:"')\]}]/.test(lastGlyph) ? fontSize * 0.15 : 0;
      const safeMin = measuredAdvance + Math.max(fontSize * 0.08, 3) + punctBump;
      if (safeMin > emittedWidth) {
        if (typeof console !== 'undefined') {
          console.warn('[Export] Text width bumped for advance safety', {
            elementId: element.id,
            type: element.type,
            stored: emittedWidth,
            safeMin,
          });
        }
        emittedWidth = safeMin;
      }
    }
    styles.push(`width: ${Math.ceil(emittedWidth)}px`);
  } else if (element.width) {
    styles.push(`width: ${Math.round(element.width)}px`);
  }

  // Descender-aware height for text/button: belt-and-suspenders for legacy
  // projects whose stored `element.height` predates the text metrics fix.
  // Guarantees the last line's descender (g, j, p, q, y) is not clipped by
  // the overlay's `overflow: hidden`. Math.ceil, never Math.round.
  let emittedHeight = element.height ?? 0;
  if (element.type === 'text' || element.type === 'button') {
    const t = element as TextElement | ButtonElement;
    const text = (t as { text?: string }).text || '';
    const measured = measureTextBlockHeight({
      lines: text.split('\n'),
      fontSize: t.fontSize || 16,
      fontFamily: t.fontFamily || 'Arial',
      fontWeight: (t as { fontWeight?: number | string }).fontWeight ?? 400,
      fontStyle: (t as { fontStyle?: string }).fontStyle || 'normal',
      lineHeight: (t as { lineHeight?: number }).lineHeight ?? 1.16,
    });
    if (measured.height > emittedHeight + 1) {
      if (typeof console !== 'undefined') {
        console.warn('[Export] Text height bumped for descender safety', {
          elementId: element.id,
          stored: emittedHeight,
          measured: measured.height,
          method: measured.method,
        });
      }
      emittedHeight = measured.height;
    }
    if (emittedHeight > 0) styles.push(`height: ${Math.ceil(emittedHeight)}px`);
  } else if (element.height) {
    styles.push(`height: ${Math.round(element.height)}px`);
  }

  const zIdx = allElements.findIndex(el => el.id === element.id);
  styles.push(`z-index: ${zIdx + 1}`);

  if (element.type === 'text') {
    // Turn 1: text is baked into composite JPEG. Overlay emits invisible accessibility layer.
    const t = element as TextElement;
    styles.push('opacity: 0');
    styles.push('pointer-events: none');
    styles.push('overflow: hidden');
    styles.push('margin: 0');
    if (t.fontFamily) styles.push(`font-family: '${t.fontFamily}'`);
    if (t.fontSize) styles.push(`font-size: ${t.fontSize}px`);
  }

  if (element.type === 'button') {
    // Turn 1: button visual is baked into composite JPEG. Overlay emits invisible clickable region.
    const b = element as ButtonElement;
    styles.push('opacity: 0');
    styles.push('cursor: pointer');
    styles.push('display: block');
    styles.push('text-decoration: none');
    styles.push('overflow: hidden');
    if (b.fontFamily) styles.push(`font-family: '${b.fontFamily}'`);
    if (b.fontSize) styles.push(`font-size: ${b.fontSize}px`);
    // Transparent buttons: bake nothing into the JPEG and mark the overlay
    // entry semantically. The click region stays active for accessibility.
    if (b.backgroundColor === 'transparent') {
      styles.push('background-color: transparent');
      if (b.border?.color === 'transparent') styles.push('border-color: transparent');
      styles.push('pointer-events: auto');
    }
  }

  if (element.type === 'shape') {
    const s = element as ShapeElement;
    const shapeFallback = s.fill || 'transparent';
    const { elementMappings } = useMappingStore.getState();
    if (hasResolvedFillMapping(s, elementMappings, 'fillColor')) {
      styles.push(`background: ${shapeFallback}`);
    } else {
      styles.push(`background: ${fillConfigToCss(s.shapeFill, shapeFallback)}`);
    }

    const bw = s.border?.width ?? s.strokeWidth ?? 0;
    const bc = s.border?.color ?? s.stroke ?? '';
    if (bw > 0 && bc) styles.push(`border: ${bw}px ${s.border?.style ?? 'solid'} ${bc}`);
    if (s.border?.radius) {
      const r = s.border.radius;
      styles.push(r.linked
        ? `border-radius: ${r.topLeft}px`
        : `border-radius: ${r.topLeft}px ${r.topRight}px ${r.bottomRight}px ${r.bottomLeft}px`);
    } else if (s.borderRadius) {
      styles.push(`border-radius: ${s.borderRadius}px`);
    }
    if (s.shapeType === 'circle') styles.push('border-radius: 50%');

    if (typeof s.fillOpacity === 'number' && s.fillOpacity !== 1) {
      // Apply fillOpacity on top of the element opacity, matching the editor.
      const combined = (element.opacity ?? 1) * s.fillOpacity;
      // Override any earlier emitted opacity by re-pushing; element.opacity
      // branch below is skipped when we push here.
      styles.push(`opacity: ${combined}`);
    }

    const sShadow = shadowToBoxShadowCss(s.shadow);
    if (sShadow) styles.push(`box-shadow: ${sShadow}`);
  }

  if (element.type === 'image') {
    // Turn 1: image is baked into composite JPEG. Overlay emits invisible accessibility marker.
    styles.push('opacity: 0');
    styles.push('pointer-events: none');
    styles.push('overflow: hidden');
  }

  if (element.type === 'svg') {
    // Turn 1: SVG is baked into composite JPEG. Overlay emits invisible accessibility marker.
    styles.push('opacity: 0');
    styles.push('pointer-events: none');
    styles.push('overflow: hidden');
  }

  // Only emit element.opacity when we haven't already written a combined
  // opacity for shapes with fillOpacity.
  const shapeHasFillOpacity =
    element.type === 'shape' &&
    typeof (element as ShapeElement).fillOpacity === 'number' &&
    (element as ShapeElement).fillOpacity !== 1;
  if (!shapeHasFillOpacity && element.opacity !== undefined && element.opacity !== 1) {
    styles.push(`opacity: ${element.opacity}`);
  }
  if (element.rotation && element.rotation !== 0) {
    styles.push(`transform: rotate(${element.rotation}deg)`);
    styles.push(`transform-origin: 0 0`);
  }

  return styles.join('; ') + ';';
}

export function generateOverlayPacket(
  resolvedElements: ResolvedElement[],
  legacyId: string,
  projectName: string,
  canvasWidth: number,
  canvasHeight: number,
  buttonUrls: ButtonUrlAssignment[],
  storeId: string = '0',
  options?: {
    packetFilenameOverride?: string;
    backgroundImageFilenameOverride?: string;
  }
): { json: string; blob: Blob; filename: string } {
  const filename = options?.packetFilenameOverride
    || generatePacketFilename(legacyId, projectName, storeId);

  const overlayElements = resolvedElements
    .filter((re) => re.resolved.layer === 'OVERLAY_LAYER' && re.resolved.visible)
    .sort((a, b) => a.resolved.zIndex - b.resolved.zIndex);

  const allResolved = resolvedElements.map(re => re.resolved);
  const overlayResolved = overlayElements.map(re => re.resolved);

  const htmlTagMap = assignHtmlTags(overlayResolved);
  const buttonUrlMap = new Map<string, string>();
  for (const a of buttonUrls) {
    buttonUrlMap.set(a.elementId, a.url);
  }

  const usedIds = new Set<string>();
  const layers: ExportLayer[] = [];

  for (const el of overlayResolved) {
    const layerId = slugifyLayerId(el.name || `layer-${el.type}`, usedIds);
    const htmlTag = htmlTagMap.get(el.id) || 'p';

    const layer: ExportLayer = {
      id: layerId,
      type: getLayerType(el),
      content: getElementContent(el),
      htmlTag,
      responsive: {
        desktop: {
          rawCSS: generateRawCSS(el, allResolved),
        },
      },
    };

    if (layer.type === 'link') {
      const btn = el as ButtonElement;
      layer.url = buttonUrlMap.get(el.id) || btn.href || '';
      // Screen readers need something to announce even when the visible text
      // is empty or its color is transparent — fall back to element.name.
      layer.ariaLabel = btn.text || btn.name || 'Link';
      layer.htmlTag = 'a';
    }

    if (el.type === 'image') {
      const imgEl = el as ImageElement;
      layer.src = imgEl.src || '';
      layer.alt = imgEl.alt || '';
      if (imgEl.alt && imgEl.alt.trim().length > 0) {
        layer.ariaRole = 'img';
        layer.ariaLabel = imgEl.alt;
      } else {
        layer.ariaHidden = true;
      }
      layer.htmlTag = 'div';
    }

    if (el.type === 'svg') {
      const svgEl = el as SvgElement;
      layer.svgContent = '';
      if (svgEl.alt && svgEl.alt.trim().length > 0) {
        layer.ariaRole = 'img';
        layer.ariaLabel = svgEl.alt;
      } else {
        layer.ariaHidden = true;
      }
      layer.htmlTag = 'div';
    }

    if (el.type === 'text') {
      const textEl = el as TextElement;
      layer.ariaLabel = textEl.text || '';
    }

    layers.push(layer);
  }

  // Accessible reading order (el-ax-1 AX1b): ordered, resolved plain-text
  // strings for visible text-bearing elements. Order source of truth is the
  // project's persisted canvasState.readingOrder, reconciled against the
  // elements actually present in this export (deleted IDs pruned, new text
  // elements appended in default visual order). Read-only — persistence of
  // designer-arranged order is el-ax-2's modal.
  const persistedOrder = useStudioStore.getState().canvasState.readingOrder;
  const { order: readingOrder } = reconcileReadingOrder(persistedOrder, allResolved);
  const readableById = new Map(
    allResolved.filter(isReadableElement).map((el) => [el.id, el])
  );
  const accessibleText = readingOrder
    .map((id) => readableById.get(id))
    .filter((el): el is CanvasElement => !!el && el.visible)
    .map((el) => getElementContent(el))
    .filter((s) => s.trim().length > 0);

  const aspectRatio = (canvasWidth / canvasHeight).toFixed(2);
  const heroConfig: HeroConfig = {
    id: filename,
    schoolId: legacyId,
    backgroundImage: options?.backgroundImageFilenameOverride || `${filename}.jpeg`,
    canvasPx: {
      desktop: { width: canvasWidth, height: canvasHeight },
    },
    canvasRatio: {
      desktop: `${aspectRatio} / 1`,
    },
    ...(accessibleText.length > 0 ? { accessibleText } : {}),
  };

  const packet: ExportPacket = { heroConfig, layers };
  const json = JSON.stringify([packet], null, 2);
  const blob = new Blob([json], { type: 'text/plain' });

  return { json, blob, filename };
}

// ============================================================================
// EXPORT PIPELINE
// ============================================================================

export interface ExportPipelineResult {
  success: boolean;
  outputs?: ExportOutputs;
  packetFilename?: string;
  error?: string;
}

export async function runExportPipeline(
  resolvedElements: ResolvedElement[],
  projectId: string,
  projectName: string,
  width: number,
  height: number,
  config: ExportConfig,
  onProgress?: (percent: number, message: string) => void,
  templateId?: string,
  backgroundColor?: string,
  legacyId?: string,
  buttonUrls?: ButtonUrlAssignment[],
  packetOverrides?: {
    packetFilenameOverride?: string;
    backgroundImageFilenameOverride?: string;
  }
): Promise<ExportPipelineResult> {
  const outputs: ExportOutputs = {
    dimensions: { width, height },
    fileSizes: {},
  };

  let packetFilename: string | undefined;

  consumeExportFontWarnings();

  // Warm fonts up-front so the shrink-to-fit pass measures against the real
  // faces. Single-export and bulk-export both flow through this function, so
  // doing it here guarantees parity between the two pipelines.
  await ensureFontsLoaded(resolvedElements);

  // CS Rule E.4 — hard-stop if any text/button element references a font
  // that is not in cs_fonts or has not loaded. Must run AFTER ensureFontsLoaded
  // gives fonts a chance to load, but BEFORE any rendering.
  await validateFontsBeforeExport(resolvedElements);

  // ══════════════════════════════════════════════════════════════════════
  // EXPORT-BLOCKING TEXT OVERFLOW VALIDATION (Spec Lock §7, el-cwt-3)
  //
  // Final fit pass runs here — AFTER ensureFontsLoaded (so measurement uses
  // real loaded faces on a clean charWidthsCache) and BEFORE any output is
  // generated. If any visible contained text element cannot fit its
  // designer-fixed box even at the 8px floor, the export is blocked with an
  // error naming the element. Same throw→catch→failed-state path as
  // validateFontsBeforeExport: single export, bulk export, and unbranded
  // export all inherit the block with zero store/dialog changes.
  // ══════════════════════════════════════════════════════════════════════
  const overflowErrors: string[] = [];
  for (const re of resolvedElements) {
    if (re.resolved.type === 'text') {
      const textEl = re.resolved as TextElement;
      const mode = textEl.textFitMode;
      if (mode === 'contain-within-textbox' || mode === 'contain-within-structure') {
        const { overflow } = applyTextFit(textEl);
        if (overflow && textEl.visible) {
          overflowErrors.push(
            `Element "${textEl.name || textEl.id}" — content does not fit its textbox even at the 8px minimum font size. Shorten the text, enlarge the textbox, or switch the element to Natural fit mode.`
          );
        }
      }
    }
  }
  if (overflowErrors.length > 0) {
    const summary = overflowErrors.length === 1
      ? overflowErrors[0]
      : `${overflowErrors.length} elements overflow:\n` + overflowErrors.map((e, i) => `${i + 1}. ${e}`).join('\n');
    throw new Error(`Export blocked — text overflow validation failed:\n${summary}`);
  }

  try {
    if (config.includeBackground) {
      onProgress?.(10, 'Rendering composite background (text + visuals baked in)...');

      // Turn 1: "background" now means the fully composited image with text/buttons baked in.
      // This becomes the visual source of truth. Overlay layer is invisible accessibility only.
      const backgroundBlob = await generateCompositeImage(
        resolvedElements,
        width,
        height,
        config,
        backgroundColor
      );

      outputs.backgroundBlob = backgroundBlob;
      outputs.fileSizes.background = backgroundBlob.size;

      onProgress?.(40, 'Composite background complete');
    }

    if (config.includeOverlay) {
      onProgress?.(50, 'Generating overlay packet...');

      const effectiveLegacyId = (legacyId && legacyId.trim()) ? legacyId.trim() : UNBRANDED_LEGACY_ID;
      const effectiveButtonUrls = buttonUrls || [];

      const { blob: overlayBlob, filename } = generateOverlayPacket(
        resolvedElements,
        effectiveLegacyId,
        projectName,
        width,
        height,
        effectiveButtonUrls,
        '0',
        packetOverrides
      );

      outputs.overlayBlob = overlayBlob;
      outputs.fileSizes.overlay = overlayBlob.size;
      packetFilename = filename;

      onProgress?.(70, 'Overlay packet complete');
    }

    if (config.includeComposite) {
      onProgress?.(80, 'Rendering composite image...');
      try {
        const compositeBlob = await generateCompositeImage(
          resolvedElements,
          width,
          height,
          config,
          backgroundColor
        );
        outputs.compositeBlob = compositeBlob;
        outputs.fileSizes.composite = compositeBlob.size;
        onProgress?.(95, 'Composite image complete');
      } catch (err) {
        console.error('[Export] Composite rendering failed', err);
      }
    }

    onProgress?.(100, 'Export complete');

    const fontWarnings = consumeExportFontWarnings();
    if (fontWarnings.length > 0) {
      console.warn(
        `[Export] Font warnings (${fontWarnings.length}): some fonts did not match the official server manifest. `
        + `Review the Font Manager "Server Sync" panel to resolve.`,
        fontWarnings
      );
    }

    return { success: true, outputs, packetFilename };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown export error';
    return { success: false, error: message };
  }
}
